import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Microscope, ArrowLeft, LayoutDashboard, Activity, Users, Leaf, TrendingUp,
  FolderKanban, Download, ShieldCheck, ScrollText, Lock, AlertTriangle, Check, MapPin, Loader2,
} from "lucide-react";
import { useAsaAuth } from "../AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TEAL = "#0d9488";

const SUBTABS = [
  { id: "overview", label: "Overview", icon: LayoutDashboard },
  { id: "geography", label: "Geography", icon: MapPin, phase2: true },
  { id: "symptoms", label: "Symptoms", icon: Activity },
  { id: "family", label: "Family History", icon: Users },
  { id: "environment", label: "Environment", icon: Leaf, phase2: true },
  { id: "trends", label: "Trends", icon: TrendingUp, phase2: true },
  { id: "projects", label: "Research Projects", icon: FolderKanban },
  { id: "exports", label: "Exports", icon: Download },
  { id: "governance", label: "Governance", icon: ShieldCheck },
  { id: "audit", label: "Audit", icon: ScrollText },
];

const Suppressed = ({ data }) => (
  <div className="p-8 text-center bg-amber-50 border border-amber-200 rounded-xl" data-testid="research-suppressed">
    <Lock className="mx-auto text-amber-500" />
    <p className="font-head font-semibold text-amber-800 mt-2">Insufficient data for display</p>
    <p className="text-[13px] text-amber-700 mt-1">Fewer than {data.min_cohort} eligible research records in this cohort ({data.eligible_records || 0} found). Disclosure control prevents display.</p>
  </div>
);

const Bar = ({ label, value, max, suffix = "" }) => (
  <div className="flex items-center gap-3">
    <span className="text-[13px] text-slate-600 w-40 shrink-0 truncate">{label}</span>
    <div className="flex-1 h-3 bg-slate-100 rounded-full overflow-hidden"><div className="h-full rounded-full bg-[#0d9488]" style={{ width: `${max ? (value / max) * 100 : 0}%` }} /></div>
    <span className="text-[13px] font-semibold text-slate-700 w-14 text-right">{value}{suffix}</span>
  </div>
);

const Disclaimer = () => (
  <p className="text-[12px] text-slate-400 mt-4 border-t border-slate-100 pt-3">Observed population-level associations do not establish causation. AAPI is a research index, not an individual diagnosis or disease-risk score.</p>
);

export default function ResearchObservatory() {
  const navigate = useNavigate();
  const { user, ready } = useAsaAuth();
  const [tab, setTab] = useState("overview");
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(false);
  const [algo, setAlgo] = useState(null);

  useEffect(() => { if (ready && (!user || user.role !== "admin")) navigate("/asa/admin"); }, [ready, user, navigate]);

  const load = async (id) => {
    const map = { overview: "overview", symptoms: "symptoms", family: "family-history", projects: "projects", governance: "governance", audit: "audit" };
    if (!map[id]) return;
    setLoading(true);
    try {
      const { data: d } = await axios.get(`${API}/research/${map[id]}`);
      setData((p) => ({ ...p, [id]: d }));
    } catch { toast.error("Could not load research data"); }
    setLoading(false);
  };

  useEffect(() => { load(tab); if (!algo) axios.get(`${API}/research/algorithm`).then((r) => setAlgo(r.data)).catch(() => {}); /* eslint-disable-next-line */ }, [tab]);

  const gov = data.governance?.governance;

  return (
    <div className="min-h-screen bg-[#f6f8f8]">
      <header className="bg-[#0b3b38] text-white">
        <div className="max-w-6xl mx-auto px-4 py-5 flex items-center gap-3">
          <button onClick={() => navigate("/asa/admin")} data-testid="research-back" className="inline-flex items-center gap-1.5 text-white/80 hover:text-white text-sm"><ArrowLeft size={16} /> Admin</button>
          <div className="flex items-center gap-2 ml-2">
            <Microscope size={22} />
            <div>
              <h1 className="font-head font-bold text-lg leading-tight">ASAL Research Observatory</h1>
              <p className="text-white/70 text-[12px]">Population-level analytics · privacy-protected · no individual identifiers</p>
            </div>
          </div>
          <span className={`ml-auto text-[11px] font-semibold px-2.5 py-1 rounded-full ${gov?.activated ? "bg-[#22a35a] text-white" : "bg-amber-400 text-amber-900"}`} data-testid="research-activation-badge">
            {gov?.activated ? "ACTIVATED" : "NOT ACTIVATED (governance gate)"}
          </span>
        </div>
      </header>

      {!gov?.activated && (
        <div className="bg-amber-50 border-b border-amber-200">
          <div className="max-w-6xl mx-auto px-4 py-2.5 flex items-center gap-2 text-[12.5px] text-amber-800">
            <AlertTriangle size={15} /> Research processing is <strong>not activated for production</strong>. Complete the Governance checklist before treating outputs as approved research.
          </div>
        </div>
      )}

      <div className="max-w-6xl mx-auto px-4 py-6">
        <div className="flex flex-wrap gap-2 mb-6">
          {SUBTABS.map((t) => (
            <button key={t.id} data-testid={`research-tab-${t.id}`} onClick={() => setTab(t.id)}
              className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-[13px] font-semibold transition-colors ${tab === t.id ? "bg-[#0d9488] text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-[#0d9488]"}`}>
              <t.icon size={14} /> {t.label}{t.phase2 && <span className="text-[9px] bg-slate-100 text-slate-400 px-1 rounded ml-0.5">SOON</span>}
            </button>
          ))}
        </div>

        {loading ? <div className="p-12 text-center"><Loader2 className="animate-spin text-[#0d9488] mx-auto" /></div> : (
          <div>
            {/* OVERVIEW */}
            {tab === "overview" && data.overview && (
              data.overview.suppressed ? <Suppressed data={data.overview} /> : (
                <div data-testid="research-overview">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {[["Eligible records", data.overview.eligible_records], ["Family history", data.overview.family_history_pct + "%"], ["Median symptom clusters", data.overview.median_clusters], ["Mean AAPI", data.overview.mean_aapi], ["Median AAPI", data.overview.median_aapi], ["Active projects", data.overview.active_projects], ["Regions", data.overview.regions_represented], ["Median symptoms", data.overview.median_symptoms]].map(([l, v]) => (
                      <div key={l} className="bg-white rounded-xl border border-slate-200 p-4"><p className="text-2xl font-head font-bold text-slate-800">{v}</p><p className="text-[12px] text-slate-500 mt-0.5">{l}</p></div>
                    ))}
                  </div>
                  <div className="bg-white rounded-xl border border-slate-200 p-5 mt-5">
                    <h3 className="font-head font-semibold text-slate-800 mb-3">Top reported symptom clusters</h3>
                    <div className="space-y-2.5">
                      {(data.overview.top_clusters || []).map((c) => <Bar key={c.cluster} label={c.cluster} value={c.count} max={data.overview.top_clusters[0].count} />)}
                    </div>
                    <Disclaimer />
                  </div>
                </div>
              )
            )}

            {/* SYMPTOMS */}
            {tab === "symptoms" && data.symptoms && (
              data.symptoms.suppressed ? <Suppressed data={data.symptoms} /> : (
                <div className="grid md:grid-cols-2 gap-5" data-testid="research-symptoms">
                  <div className="bg-white rounded-xl border border-slate-200 p-5">
                    <h3 className="font-head font-semibold text-slate-800 mb-3">Most reported symptoms</h3>
                    <div className="space-y-2">{data.symptoms.top_symptoms.map((s) => <Bar key={s.symptom} label={s.symptom} value={s.count} max={data.symptoms.top_symptoms[0].count} />)}</div>
                  </div>
                  <div className="bg-white rounded-xl border border-slate-200 p-5">
                    <h3 className="font-head font-semibold text-slate-800 mb-3">Symptom cluster prevalence</h3>
                    <div className="space-y-2">{data.symptoms.cluster_prevalence.map((c) => <Bar key={c.cluster} label={c.cluster} value={c.pct} max={100} suffix="%" />)}</div>
                    <Disclaimer />
                  </div>
                </div>
              )
            )}

            {/* FAMILY */}
            {tab === "family" && data.family && (
              data.family.suppressed ? <Suppressed data={data.family} /> : (
                <div data-testid="research-family">
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <div className="bg-white rounded-xl border border-slate-200 p-4"><p className="text-2xl font-head font-bold text-slate-800">{data.family.family_history_pct}%</p><p className="text-[12px] text-slate-500">Reporting family history</p></div>
                    <div className="bg-white rounded-xl border border-slate-200 p-4"><p className="text-2xl font-head font-bold text-slate-800">{data.family.mean_symptoms_with_fh}</p><p className="text-[12px] text-slate-500">Mean symptoms (with family history)</p></div>
                    <div className="bg-white rounded-xl border border-slate-200 p-4"><p className="text-2xl font-head font-bold text-slate-800">{data.family.mean_symptoms_without_fh}</p><p className="text-[12px] text-slate-500">Mean symptoms (no family history)</p></div>
                  </div>
                  <div className="bg-white rounded-xl border border-slate-200 p-5 mt-5">
                    <h3 className="font-head font-semibold text-slate-800 mb-3">By reported relationship / degree</h3>
                    <div className="space-y-2">{data.family.by_degree.map((d) => <Bar key={d.degree} label={d.degree} value={d.count} max={data.family.by_degree[0].count} />)}</div>
                    <p className="text-[12px] text-slate-400 mt-3">{data.family.note}</p>
                  </div>
                </div>
              )
            )}

            {/* PHASE 2 PLACEHOLDERS */}
            {["geography", "environment", "trends"].includes(tab) && (
              <div className="p-10 text-center bg-white rounded-xl border border-slate-200" data-testid="research-phase2">
                <Lock className="mx-auto text-slate-300" size={30} />
                <p className="font-head font-semibold text-slate-700 mt-3">Available in Phase 2</p>
                <p className="text-[13px] text-slate-500 mt-1 max-w-md mx-auto">This module needs demographic/geographic data (age band, sex, region) and approved external datasets. We'll enable it once those consented fields are collected at sign-up.</p>
              </div>
            )}

            {/* PROJECTS */}
            {tab === "projects" && data.projects && (
              <div className="space-y-4" data-testid="research-projects">
                {data.projects.projects.map((p) => (
                  <div key={p.project_id} className="bg-white rounded-xl border border-slate-200 p-5">
                    <div className="flex items-center gap-2"><span className="text-[11px] font-bold text-white bg-[#0d9488] px-2 py-0.5 rounded">{p.project_id}</span><span className="text-[11px] font-semibold text-amber-700 bg-amber-100 px-2 py-0.5 rounded capitalize">{p.governance_status}</span></div>
                    <h3 className="font-head font-semibold text-slate-800 mt-2">{p.title}</h3>
                    <p className="text-[13px] text-slate-600 mt-1"><strong>Primary objective:</strong> {p.primary_objective}</p>
                    <p className="text-[12px] text-slate-500 mt-1">{p.classification}</p>
                  </div>
                ))}
              </div>
            )}

            {/* EXPORTS */}
            {tab === "exports" && (
              <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="research-exports">
                <h3 className="font-head font-semibold text-slate-800">Controlled research exports</h3>
                <p className="text-[13px] text-slate-500 mt-1">Aggregated only — never contains names, emails or identifiers. Every export is logged.</p>
                <button data-testid="research-export-csv" onClick={() => window.open(`${API}/research/export?type=overview_csv`, "_blank")} className="mt-4 inline-flex items-center gap-2 h-10 px-5 rounded-lg bg-[#0d9488] hover:bg-[#0f766e] text-white font-semibold text-sm"><Download size={16} /> Aggregated overview CSV</button>
              </div>
            )}

            {/* GOVERNANCE */}
            {tab === "governance" && gov && <Governance data={data.governance} algo={algo} onSaved={() => load("governance")} />}

            {/* AUDIT */}
            {tab === "audit" && data.audit && (
              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="research-audit">
                <table className="w-full text-[13px]">
                  <thead className="bg-slate-50"><tr>{["When", "Who", "Action", "Detail"].map((h) => <th key={h} className="text-left px-4 py-2.5 text-[11px] font-semibold text-slate-500 uppercase">{h}</th>)}</tr></thead>
                  <tbody>{data.audit.audit.map((a) => <tr key={a.id} className="border-t border-slate-100"><td className="px-4 py-2 text-slate-500">{(a.when || "").slice(0, 19).replace("T", " ")}</td><td className="px-4 py-2">{a.who}</td><td className="px-4 py-2 font-medium">{a.action}</td><td className="px-4 py-2 text-slate-500">{a.detail}</td></tr>)}</tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function Governance({ data, algo, onSaved }) {
  const gov = data.governance;
  const [checklist, setChecklist] = useState(gov.checklist);
  const [minCohort, setMinCohort] = useState(gov.min_cohort);
  const allDone = data.checklist_keys.every((k) => checklist[k]);

  const toggle = async (k) => {
    const next = { ...checklist, [k]: !checklist[k] };
    setChecklist(next);
    try { await axios.post(`${API}/research/governance`, { checklist: { [k]: next[k] } }); } catch { toast.error("Could not save"); }
  };
  const saveCohort = async () => { try { await axios.post(`${API}/research/governance`, { min_cohort: Number(minCohort) }); toast.success("Minimum cohort updated"); } catch { toast.error("Failed"); } };
  const activate = async () => {
    try { await axios.post(`${API}/research/governance`, { activated: !gov.activated }); toast.success(gov.activated ? "Deactivated" : "Activated"); onSaved(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-5" data-testid="research-governance">
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <h3 className="font-head font-semibold text-slate-800">Pre-launch governance gate</h3>
        <p className="text-[13px] text-slate-500 mt-1">All items must be reviewed before research processing is activated for production. Ticking a box records that it has been reviewed — it does not constitute legal or ethical approval.</p>
        <div className="grid sm:grid-cols-2 gap-2 mt-4">
          {data.checklist_keys.map((k) => (
            <button key={k} data-testid={`gov-check-${k}`} onClick={() => toggle(k)} className={`flex items-center gap-2 text-left px-3 py-2.5 rounded-lg border text-[13px] transition-colors ${checklist[k] ? "border-[#0d9488] bg-[#e6f4f2] text-[#0f766e]" : "border-slate-200 text-slate-600 hover:border-slate-300"}`}>
              <span className={`w-4 h-4 rounded flex items-center justify-center shrink-0 ${checklist[k] ? "bg-[#0d9488] text-white" : "border border-slate-300"}`}>{checklist[k] && <Check size={11} />}</span>
              {data.labels[k]}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-3 mt-5 pt-4 border-t border-slate-100">
          <div className="flex items-center gap-2"><label className="text-[13px] text-slate-600">Minimum cohort (N)</label><input type="number" value={minCohort} onChange={(e) => setMinCohort(e.target.value)} className="w-20 h-9 border border-slate-200 rounded-lg px-2 text-sm" /><button onClick={saveCohort} className="text-[13px] font-semibold text-[#0f766e]">Save</button></div>
          <button data-testid="research-activate" onClick={activate} disabled={!allDone && !gov.activated} className={`ml-auto inline-flex items-center gap-2 h-10 px-5 rounded-lg font-semibold text-sm transition-colors ${gov.activated ? "bg-rose-100 text-rose-700" : allDone ? "bg-[#0d9488] text-white" : "bg-slate-100 text-slate-400 cursor-not-allowed"}`}>
            {gov.activated ? "Deactivate research processing" : allDone ? "Activate research processing" : "Complete checklist to activate"}
          </button>
        </div>
      </div>

      {algo && (
        <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="research-algorithm">
          <h3 className="font-head font-semibold text-slate-800">Algorithm registry — {algo.algorithm_id} {algo.version}</h3>
          <p className="text-[12px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2.5 mt-2">{algo.validation_status}</p>
          <p className="text-[13px] text-slate-600 mt-3">{algo.definition}</p>
          <div className="mt-3 text-[13px] text-slate-600"><strong>Weights:</strong> Symptom Pattern {algo.weights.symptom_pattern} · Family History {algo.weights.family_history}</div>
        </div>
      )}
    </div>
  );
}
